import { useEffect, useState } from "react";
import { MenuOption, Table, TableColumn, TableMenu } from "../../components/Table";
import InventoryHeader from "./InventoryHeader";
import CategoryFilter from "./CategoryFilter";
import AddItemModal from "../../Modal/orders/AddItemModal";
import { initAllStores } from "../../utils/cache";
import type Room from "../../models/Room";
import FullPageSpinner from "../../components/FullPageSpinner";
import { v4 as uuidv4 } from "uuid";
import { useModal } from "../../context/ConfirmModalContext";
import { useToast } from "../../context/ToastContext";
import { capitalizeFirst } from "../../utils/string";
import { errorMessage, sendRequest } from "../../utils/api";
import { isEmptyObject, isOnline } from "../../utils/helper-function";
import { PAGINATION_PAGE_NUMBER } from "../../constants";
import { terminal } from "../../utils/user-helper";
import { syncServices } from "../../utils/sync";


const initFormData = {
    id: "",
    name: "",
    newCategory: "",
    price: "",
    // stock: undefined,
    // status: "Available",
};

// Columns
const columns: TableColumn[] = [
    { key: "number", label: "ID" },
    { key: "name", label: "Room" },
    { key: "price", label: "Price" },
    {
        key: "status",
        label: "Status",
        getClassName: (value: string) =>
            value === "Occupied" ? "text-red-600" : "text-green-600",
        responsive: "md",
    },
];

const RoomsInventoryPage = () => {

    const { openModal, closeModal } = useModal();
    const { showToast } = useToast();

    const [terminalId] = useState(() => terminal());

    const [category, setCategory] = useState("all");
    const [search, setSearch] = useState("");
    const [rooms, setRooms] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
    const [isEdit, setIsEdit] = useState(false);
    const [autoFillData, setAutoFillData] = useState(initFormData);

    // Fetch from cache
    const loadRooms = async () => {
        const cache = await initAllStores();
        setLoading(true);
        try {
            // const data: Room[] = await cache.getAllBy("rooms", { terminal: terminalId }, "number");

            const getItems = async (): Promise<(Room)[]> => {
                return await cache.getAllBy(
                    "rooms", { terminal: terminalId }, "number"
                );
            }

            
            let rooms = await getItems();

            if (!isEmptyObject(rooms)) {
                await syncServices(["rooms"]);
                rooms = await getItems();
            }

            const transformed = rooms.map((r: Room) => ({
                id: r.id,
                name: capitalizeFirst(r.name),
                number: r.number.toUpperCase(),
                price: r.cashPrice.toLocaleString(),
                cashPrice: r.cashPrice,
                // isOccupied: r.isOccupied,
                status: r.isOccupied ? "Occupied" : "Available",
            }));

            setRooms(transformed);
        } catch (err) {
            console.error("Failed to load rooms:", err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadRooms();
    }, []);

    // Build categories dynamically
    const categories = [
        { id: "all", name: "All" },
        ...Array.from(new Set(rooms.map((r) => r.name))).map((name, idx) => ({
            id: name,
            name,
        })),
    ];

    // Filter rooms
    const filteredRooms = rooms.filter(
        (r) =>
            (category === "all" || r.name === category) &&
            r.name.toLowerCase().includes(search.toLowerCase())
    );

    const showConfirmationModal = (data: any) => {

        const room = {
            updatedAt: new Date(),
            createdAt: new Date(),
            id: data?.item?.id || uuidv4(),
            name: capitalizeFirst(data?.item?.name ?? ""),
            number: data?.item?.newCategory,
            isOccupied: false,
            cashPrice: parseFloat(data?.item?.price),
            terminal: terminalId,
        };

        // ✅ Check required fields
        const missingFields: string[] = [];
        if (!room.name) missingFields.push("Room Name");
        if (!room.number) missingFields.push("Room Number");
        if (!room.cashPrice) missingFields.push("Room Price");

        if (missingFields.length > 0) {
            return openModal(
                "Missing Fields",
                `Please fill in: ${missingFields.join(", ")}`,
                closeModal,
                false,
                "Close"
            );
        }

        //✅ Check for duplicate room
        const isExist = rooms.find(
            (rm: Room) =>
                rm.number.toLocaleLowerCase() === room.number.toLowerCase() &&
                rm.id !== room.id
        );

        if (isExist) {
            return openModal(
                "Duplicate Room Detected",
                `A room with number "${room.number.toUpperCase()}" already exists.`,
                closeModal,
                false,
                "Close"
            );
        }

        // ✅ Confirm modal before saving
        openModal(
            isEdit ? "Confirm Update" : "Add New Room",
            isEdit
                ? "Are you sure you want to update this room?"
                : (
                    "You're about to add a new room: "
                    + `"${capitalizeFirst(room.name)}" (Room No. ${room.number}) `
                    + `under the "${capitalizeFirst(room.name)}" category.`
                ),
            () => {
                if (isEdit) updateRoom(room);
                else createRoom(room);
            }
        );
    };

    const updateRoom = async (roomData: any) => {
        const cache = await initAllStores();
        setLoading(true);

        const { id, ...dataWithNoId } = roomData;

        try {

            await Promise.all([
                sendRequest("PUT", `/rooms/${id}/update`, dataWithNoId),
                cache.updateManyBy("rooms", [id], roomData),
            ]);

            setRooms((prev) =>
                prev.map((it) =>
                    it.id === id
                        ? {
                            ...it,
                            ...roomData,
                            price: roomData.cashPrice.toLocaleString(),
                            name: capitalizeFirst(it.name),
                            number: it?.number?.toUpperCase(),
                            status: !roomData.isOccupied ? "Available" : "Unavailable",
                        }
                        : it
                )
            );
            showToast("Room Updated Successfully", { type: "success" });
        } catch (error: any) {
            console.log(error);
            const err = errorMessage(error.status);
            if (err) openModal(err.title, err.msg, closeModal, false, "Close");
        } finally {
            setLoading(false);
        }
    };

    const createRoom = async (roomData: any) => {
        const cache = await initAllStores();
        setIsModalOpen(false);
        setLoading(true);

        try {
            if (!isOnline()) {
                const err = errorMessage(408);
                if (err) openModal(err.title, err.msg, closeModal, false, "Close");
                return;
            }

            await Promise.all([
                sendRequest("POST", "/rooms", roomData),
                cache.saveOne("rooms", {
                    ...roomData,
                    isOccupied: roomData.isOccupied
                }),
            ]);

            // Update UI state
            setRooms((prev) => [
                ...prev,
                {
                    id: roomData.id,
                    name: capitalizeFirst(roomData.name),
                    number: roomData.number.toUpperCase(),
                    price: roomData.cashPrice.toLocaleString(),
                    status: roomData.isOccupied ? "Occupied" : "Available",
                },
            ]);
            showToast("Room Added Successfully", { type: "success" });

        } catch (error: any) {
            console.log(error);
            const err = errorMessage(error.status);
            if (err) openModal(err.title, err.msg, closeModal, false, "Close");
        } finally {
            setLoading(false);
        }
    };

    // Delete room
    const deleteRoom = async (id: string, roomNo: string) => {
        const cache = await initAllStores();
        try {
            setLoading(true);

            await Promise.all([
                sendRequest("DELETE", `/rooms/${id}/delete`),
                cache.deleteOne("rooms", id),
                loadRooms(),
                showToast("Room Deleted Successfully", { type: "success" }),
            ]);
        } catch (err) {
            console.error("Error deleting room:", err);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex-1 p-4 md:p-6 space-y-6 overflow-y-auto">
            <InventoryHeader
                title="Rooms"
                addLabel="Add New Room"
                onAdd={() => {
                    // setSelectedRoom(null);
                    setAutoFillData(initFormData);
                    setIsEdit(false);
                    setIsModalOpen(true);
                }}
            />

            <CategoryFilter
                categories={categories}
                activeCategory={category}
                onSelect={setCategory}
            />

            <div className="flex flex-col sm:flex-row justify-between gap-3 items-center">

                <AddItemModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    type="Room"
                    categories={categories}
                    onSave={showConfirmationModal}
                    isEdit={isEdit}
                    autofillData={autoFillData}
                />

                <FullPageSpinner show={loading} />

                <input
                    type="text"
                    placeholder="Search room..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="border border-gray-300 rounded-lg px-4 py-2 w-full sm:w-1/2 focus:ring focus:ring-blue-300"
                />
                {/* <select className="border border-gray-300 rounded-lg px-3 py-2">
                    <option>Sort by</option>
                    <option>Price</option>
                    <option>Status</option>
                    <option>Recently Added</option>
                </select> */}
            </div>

            {loading ? (
                <div className="text-center py-10 text-gray-500">Loading rooms...</div>
            ) : (
                <Table
                    columns={columns}
                    data={filteredRooms}
                    rowsPerPage={PAGINATION_PAGE_NUMBER}
                    tableMenu={(row: any, closeMenu) => (
                        <TableMenu
                            row={row}
                            closeMenu={closeMenu}
                            options={[
                                {
                                    label: "Room Details",
                                    color: "text-blue-500",
                                    onClick: (r: any) => {
                                        console.log(r)
                                        openModal(
                                            "Room Details",
                                            `-------------------------------------------
                                            Number: ${r.number}
                                            Type: ${r.name}
                                            Rate: ₦${r.cashPrice.toLocaleString()}
                                            Status: ${r.status === "Occupied" ? "❌ Occupied" : "✅ Available"}

                                            -------------------------------------------`,
                                            closeModal,
                                            false,
                                            "Close"
                                        );
                                    },
                                },
                                {
                                    label: "Edit Room",
                                    color: "text-blue-500",
                                    onClick: () => {
                                        setIsEdit(true);
                                        // console.log(row)
                                        setAutoFillData({
                                            id: row.id,
                                            name: row.name,
                                            newCategory: row.number,
                                            price: row.price.replace(",", ""),
                                        });
                                        // setSelectedRoom(row);
                                        setIsModalOpen(true);
                                        closeMenu();
                                    },
                                },
                                {
                                    label: "Delete Room",
                                    color: "text-red-500",
                                    onClick: (r: any) => {
                                        if (r.status !== "Available") {
                                            return openModal(
                                                "Action Blocked ⚠️",
                                                `Room ${r.number} is currently occupied and cannot be deleted. Please check out the guest before attempting to delete this room.`,
                                                closeModal,
                                                false,
                                                "Close"
                                            );
                                        }

                                        // Confirm deletion modal
                                        openModal(
                                            "Confirm Delete Room",
                                            `Are you sure you want to delete Room ${r.number}? This action cannot be undone.`,
                                            async () => {
                                                // Perform deletion logic here
                                                await deleteRoom(r.id, r.number);
                                            },
                                            true, "Yes, Delete", "Cancel", "red"
                                        );
                                    }
                                },
                            ] as MenuOption<Room>[]}
                        />
                    )}
                />
            )}

            {/* Add / Edit Modal */}
            {/* {showAddModal && (
        <AddItemModal
          title={selectedRoom ? "Edit Room" : "Add New Room"}
          onClose={() => setShowAddModal(false)}
          onSave={handleSave}
          defaultData={selectedRoom || undefined}
          fields={[
            { label: "Room Name", key: "name", type: "text" },
            { label: "Room Number", key: "number", type: "text" },
            { label: "Price", key: "price", type: "number" },
          ]}
        />
      )} */}


            {/* Confirm Delete Modal */}
            {/* {showConfirmModal && (
        <ConfirmModal
          title="Confirm Delete"
          message="This action cannot be undone. Do you wish to continue?"
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={confirmAction}
        />
      )} */}
        </div>
    );
};

export default RoomsInventoryPage;